/**
 * Parses a Chrome / Firefox / Edge bookmark export (Netscape Bookmark File
 * Format) and returns the list of `<a href>` links inside, optionally scoped
 * to a single named folder.
 *
 * Folder filtering: locates the `<H3>` whose trimmed text matches `folder`,
 * then collects every `<a href>` inside the immediately-following `<DL>`
 * (recursive — sub-folders count). Without a filter, returns every link in
 * the whole document.
 *
 * URLs whose scheme is browser-internal (chrome://, edge://, about:, etc.)
 * are returned in `skipped` so the UI can surface a count and the API isn't
 * spammed with requests it would reject anyway.
 */

const SKIPPED_SCHEME =
  /^(chrome|chrome-extension|edge|brave|about|view-source|file|data|javascript):/i;

export interface RawBookmark {
  url: string;
  title: string;
}

export interface ParseResult {
  importable: RawBookmark[];
  skipped: RawBookmark[];
  folderFound: boolean;
}

export function parseBookmarksHtml(html: string, folder?: string): ParseResult {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  const folderName = folder?.trim();
  let scope: ParentNode = doc;

  if (folderName) {
    const headings = doc.querySelectorAll('h3');
    const target = Array.from(headings).find((h) => (h.textContent ?? '').trim() === folderName);
    if (!target) {
      return { importable: [], skipped: [], folderFound: false };
    }
    // The folder's contents live in the immediately-following <DL>.
    const dl = target.nextElementSibling;
    if (!dl || dl.tagName.toLowerCase() !== 'dl') {
      return { importable: [], skipped: [], folderFound: false };
    }
    scope = dl;
  }

  const anchors = scope.querySelectorAll('a[href]');
  const importable: RawBookmark[] = [];
  const skipped: RawBookmark[] = [];

  for (const a of Array.from(anchors)) {
    const url = a.getAttribute('href') ?? '';
    const title = (a.textContent ?? '').trim() || url;
    if (!url) continue;
    if (SKIPPED_SCHEME.test(url)) {
      skipped.push({ url, title });
    } else {
      importable.push({ url, title });
    }
  }

  return { importable, skipped, folderFound: true };
}
